import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue with recoil import
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Layout from '../../../../components/Learner/Layout';
import SpeakingPracticeComponent from '../../../../components/Learner/SpeakingPractice';
import { getSpeakingPracticeQuestions, submitPractice, getPracticeStatus } from '../../../../services/practiceApi';
import { usePolling } from '../../../../hooks/usePolling';
// @ts-ignore - TypeScript casing issue with atoms import
import { courseTreeAtom, courseTreeBaselineAtom, speakingAudioRecordingsState } from '../../../../recoil/atoms';
import { findCourseIdBySubcourseId, findLangCodeBySubcourseIdWithFallback } from '../../../../utils/courseTreeUtils';
import { 
  getPracticeParams as getPracticeParamsUtil, 
  formatSpeakingUserAnswers,
  isAllPronunciation,
  clearPracticeStorage,
} from '../../../../utils/practiceUtils';
import { usePracticeSubmission, usePollingResult } from '../../../../hooks/usePracticeSubmission';
import { usePracticeSession, isPageReload } from '../../../../hooks/usePracticeSession';
import {
  PracticeErrorState,
  PracticeNoQuestionsState,
} from '../../../../components/Learner/Practice/PracticePageStates';
import PracticeLoadingOverlay from '../../../../components/Learner/Practice/PracticeLoadingOverlay';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import {
  SPEAKING_GRADING_POLL_500_EXHAUSTED_FOOTER,
  SPEAKING_GRADING_POLL_500_FIRST_FALLBACK,
} from '../../../../constants/speakingPracticeMessages';
import './QuickPractice.css';

const SpeakingPractice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseTree = useRecoilValue(courseTreeAtom);
  const courseTreeBaseline = useRecoilValue(courseTreeBaselineAtom);
  // changed by Cursor: reset Recoil audio state when starting new session (replaces localStorage clear)
  const setAudioRecordings = useSetRecoilState(speakingAudioRecordingsState);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [pollingRetryCount, setPollingRetryCount] = useState(0);
  const [gradingFatalError, setGradingFatalError] = useState(null);
  const requestIdRef = useRef(null);
  /** After user taps Try Again once following poll HTTP 500, next 500 shows Go Home only. */
  const poll500UserRetriedRef = useRef(false);

  const { saveSession, loadSession, clearSession } = usePracticeSession('speaking-practice');

  useEffect(() => {
    poll500UserRetriedRef.current = false;
  }, [sessionData?.['session-id']]);

  // changed by Cursor: sync ref immediately so polling can use it before re-render
  const setRequestIdWithRef = useCallback((id) => {
    requestIdRef.current = id;
    setRequestId(id);
  }, []);

  // Get practice parameters from location state or URL params
  const getPracticeParams = () => getPracticeParamsUtil(location);

  // Track if fetch is in progress to prevent duplicate calls
  const isFetchingRef = useRef(false);
  const lastLocationRef = useRef(null);

  // Fetch questions from backend, or restore from localStorage on refresh
  useEffect(() => {
    const locationKey = location.pathname + location.search;
    if (isFetchingRef.current) return;
    if (lastLocationRef.current === locationKey && questions.length > 0) return;

    const params = getPracticeParams();
    const hasParams = !!(params.lessonId || params.skillId || params.subcourseId || params.courseId);

    // Restore from localStorage on page reload, or when there are no API params
    if (isPageReload() || !hasParams) {
      const saved = loadSession();
      if (saved?.sessionData && saved?.questions?.length > 0) {
        lastLocationRef.current = locationKey;
        setSessionData(saved.sessionData);
        setQuestions(saved.questions);
        setLoading(false);
        return;
      }
      if (!hasParams) {
        const errorMsg = 'Missing required parameters. Please navigate from a course, subcourse, skill, or lesson page.';
        setError(errorMsg);
        setLoading(false);
        return;
      }
    }

    const fetchQuestions = async () => {
      isFetchingRef.current = true;
      lastLocationRef.current = locationKey;
      setLoading(true);
      setError(null);

      const result = await getSpeakingPracticeQuestions(params);

      if (result.success && result.data) {
        const treeForLookup = courseTree || courseTreeBaseline;
        let courseId = params.courseId || result.data['course-id'];
        if (!courseId && params.subcourseId && treeForLookup) {
          courseId = findCourseIdBySubcourseId(treeForLookup, params.subcourseId);
        }

        const subcourseId = params.subcourseId || result.data['subcourse-id'];
        const langCode = findLangCodeBySubcourseIdWithFallback(
          subcourseId,
          courseTree,
          courseTreeBaseline
        );

        const sessionDataWithParams = {
          ...result.data,
          'lesson-id': params.lessonId || result.data['lesson-id'],
          'course-id': courseId || result.data['course-id'],
          'subcourse-id': subcourseId,
          'skill-id': params.skillId || result.data['skill-id'],
          'lang-code': langCode,
        };

        const sessionId = sessionDataWithParams['session-id'];
        clearPracticeStorage(sessionId, 'speaking-practice', true);
        setAudioRecordings({});

        setSessionData(sessionDataWithParams);
        const problems = result.data.problems || [];
        setQuestions(problems);
        saveSession(sessionDataWithParams, problems, location.state);
      } else {
        const errorMsg = result.message || 'Failed to load practice questions';
        console.error('❌ SpeakingPractice - API error:', errorMsg, result);
        setError(errorMsg);
      }

      setLoading(false);
      isFetchingRef.current = false;
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, courseTree, courseTreeBaseline]);

  // Re-resolve and persist lang-code when session was stored with null (restore or race with empty Recoil tree).
  useEffect(() => {
    if (!sessionData) return;
    if (sessionData['lang-code']) return;
    const subId = sessionData['subcourse-id'];
    if (!subId) return;
    const resolved = findLangCodeBySubcourseIdWithFallback(subId, courseTree, courseTreeBaseline);
    if (!resolved) return;
    const next = { ...sessionData, 'lang-code': resolved };
    setSessionData(next);
    saveSession(next, questions, location.state);
  }, [sessionData, courseTree, courseTreeBaseline, questions, location.state, saveSession]);

  // Polling hook — uses ref so the first immediate poll already has the requestId
  const { data: statusData, startPolling, stopPolling, resetData: resetPollingData } = usePolling(
    () => requestIdRef.current
      ? getPracticeStatus(requestIdRef.current, {
          page: 'SpeakingPractice',
          action: 'poll-status',
          practiceSessionId: sessionData?.['session-id'] || null,
        })
      : Promise.resolve(null),
    7000,
    (result) => {
      // Stop polling only if:
      // 1. Status is 'ok' AND has data (completed successfully)
      // 2. HTTP 500 — BE itself is broken (fatal, not retryable)
      // Continue polling for 'processing' or '200 + error' (retryable per async grading contract)
      if (!result) return false;

      if (result.status === 'ok' && result.data) return true;
      if (result.httpStatus >= 500) return true;
      return false;
    }
  );

  // Cleanup polling on unmount or when navigating away
  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only cleanup on unmount - stopPolling is stable from usePolling hook

  // changed by Cursor: onSubmitError supaya error bar muncul dan user bisa "Try again" tanpa balik ke soal pertama
  const handleSubmitError = (message) => {
    setSubmissionError(message || 'Submission error. Try again');
  };

  // Submission hook
  // Submission hook
  const { handleSubmit, isSubmitting, submittedAnswers, resetSubmitting } = usePracticeSubmission({
    questions,
    sessionData,
    formatUserAnswers: formatSpeakingUserAnswers,
    practiceType: 'speaking-practice',
    getSubpracticeType: (questions) => isAllPronunciation(questions) ? 'pronunciation' : 'speaking',
    stopPolling,
    startPolling,
    setRequestId: setRequestIdWithRef,
    shouldRetry: (questions) => !isAllPronunciation(questions), // Only retry for speaking, not pronunciation
    onSubmitError: handleSubmitError,
  });

  const isMockPolling = process.env.REACT_APP_MOCK_POLLING_ERROR_200 === 'true'
    || process.env.REACT_APP_MOCK_POLLING_ERROR_500 === 'true';

  // Handle polling result
  usePollingResult({
    statusData,
    isSubmitting,
    stopPolling,
    sessionData,
    questions,
    submittedAnswers,
    practiceType: 'speaking-practice',
    location,
    navigate,
    maxPollingErrorRetries: isMockPolling ? 3 : 40,
    onPollingRetry: (count) => setPollingRetryCount(count),
    onSubmissionError: (info) => {
      resetSubmitting();
      setPollingRetryCount(0);
      const message =
        typeof info === 'string' ? info : (info?.message || '');
      const kind = typeof info === 'object' && info?.kind ? info.kind : null;

      if (kind === 'poll_http_500') {
        if (poll500UserRetriedRef.current) {
          setGradingFatalError({
            title: 'Grading Failed',
            message: '',
            poll500Exhausted: true,
          });
        } else {
          setGradingFatalError({
            title: 'Grading Failed',
            message: message.trim() ? message : SPEAKING_GRADING_POLL_500_FIRST_FALLBACK,
            poll500First: true,
          });
        }
        return;
      }

      poll500UserRetriedRef.current = false;
      setGradingFatalError({
        title: 'Grading Failed',
        message:
          message || 'An error occurred while grading your speaking. Please try again.',
        poll500First: false,
        poll500Exhausted: false,
      });
    },
    onGradingSuccess: () => {
      poll500UserRetriedRef.current = false;
      clearSession();
      try { localStorage.removeItem(`speaking-practice-answers-${sessionData?.['session-id']}`); } catch (_) {}
    },
  });

  const handleClose = () => {
    clearSession();
    try { localStorage.removeItem(`speaking-practice-answers-${sessionData?.['session-id']}`); } catch (_) {}

    // Priority 1: Use fromPage from location state (from English Language Learning Page)
    const fromPage = location.state?.fromPage;
    if (fromPage) {
      console.log('📖 SpeakingPractice: Navigating back to fromPage:', fromPage);
      navigate(fromPage);
      return;
    }
    
    // Priority 2: Navigate back to last opened URL
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      console.log('📖 SpeakingPractice: Navigating back to lastOpenedUrl:', lastOpenedUrl);
      navigate(lastOpenedUrl);
      return;
    }
    
    // Priority 3: Fallback to default
    console.log('📖 SpeakingPractice: No fromPage or lastOpenedUrl found, navigating to default location');
    navigate('/learner/language-learning');
  };

  // Prefer lang from session (set from course tree when questions load); fromPage is only a fallback.
  // Using fromPage alone caused wrong TTS/API lang (e.g. English voice on German course) after pronun-audio failed.
  const fromPage = location.state?.fromPage;
  let langFromRoute = 'de';
  if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
    langFromRoute = 'en';
  } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
    langFromRoute = 'ja';
  }
  const langCode = sessionData?.['lang-code'] || langFromRoute;

  // Loading state
  if (loading) {
    return <PracticeLoadingOverlay visible text="Loading practice questions..." />;
  }

  // Error state
  if (error) {
    return <PracticeErrorState title="Speaking Practice" error={error} />;
  }

  // No questions state
  if (!questions || questions.length === 0) {
    return <PracticeNoQuestionsState title="Speaking Practice" onBack={handleClose} />;
  }

  return (
    <Layout hideSidebar>
      <div className="quick-practice-page">
        {submissionError && (
          <div className="practice-submission-error" role="alert">
            <span>{submissionError}</span>
            <button
              type="button"
              data-track-action="speaking-try-again-banner"
              className="practice-try-again-btn"
              onClick={() => {
                if (!submittedAnswers || !submittedAnswers.length) return;
                setSubmissionError(null);
                resetPollingData();
                handleSubmit(submittedAnswers);
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Fatal grading error overlay (HTTP 500 or max retries exceeded) */}
        <PracticeLoadingOverlay
          visible={!!gradingFatalError}
          variant="error"
          text={gradingFatalError?.title || 'Grading Failed'}
          subtext={
            gradingFatalError?.poll500Exhausted
              ? SPEAKING_GRADING_POLL_500_EXHAUSTED_FOOTER
              : gradingFatalError?.message
          }
          showCloseButton={!gradingFatalError?.poll500First}
          retryLabel="Try Again"
          closeLabel={gradingFatalError?.poll500Exhausted ? 'Go Home' : 'Go Back'}
          onRetry={
            gradingFatalError?.poll500Exhausted
              ? undefined
              : () => {
                  if (!submittedAnswers || !submittedAnswers.length) return;
                  if (gradingFatalError?.poll500First) {
                    poll500UserRetriedRef.current = true;
                  }
                  setGradingFatalError(null);
                  setPollingRetryCount(0);
                  setSubmissionError(null);
                  resetPollingData();
                  handleSubmit(submittedAnswers);
                }
          }
          onClose={handleClose}
        />

        {/* Loading / retrying overlay */}
        <PracticeLoadingOverlay
          visible={isSubmitting && !gradingFatalError}
          text={pollingRetryCount > 0
            ? 'Still grading your answers...'
            : 'Submitting your answers...'}
          subtext={pollingRetryCount > 0
            ? `Encountered an issue, retrying\u2026 (attempt ${pollingRetryCount})`
            : 'Please wait while we grade your speaking'}
        />

        <SpeakingPracticeComponent
          practiceTitle="Speaking Practice" 
          key={sessionData?.['session-id'] || questions.length}
          questions={questions}
          onComplete={handleSubmit}
          onClose={handleClose}
          hideCompletedScreen={true}
          sessionData={sessionData}
          langCode={langCode}
        />
      </div>
    </Layout>
  );
};

export default SpeakingPractice;

